import type { CanvasVariant } from "../canvas/variant-resolver";

/**
 * Which productWithMediaVariant rows the art catalogue may sell.
 *
 * An artwork is printed as a povečava: the canvas product's variants, canvas
 * and paper alike (paper is a material axis on that same definition). Other
 * products keep variants in the same table. The map product (AddMapPrints)
 * copies every canvas variant as an active MAP- twin with identical sizes and
 * prices. When the catalogue read the whole table, each size showed twice,
 * and half the rows put a "Zemljevid" line in the cart.
 *
 * Scoped twice. The query asks the server for the canvas kind only
 * (ART_VARIANT_FILTER). The rows are then filtered again here. @dataui/crud
 * ignores a relation filter whose relation is not joined, so a dropped join
 * would otherwise bring the map rows back without any error.
 */
export const ART_DEFINITION_KIND = "canvas";

/** @dataui/crud filters for the art catalogue's variant reads. Needs join=definition. */
export const ART_VARIANT_FILTER = [
  "active||$eq||true",
  `definition.kind||$eq||${ART_DEFINITION_KIND}`,
];

/** The same filter, as a query string for raw fetches. */
export const ART_VARIANT_QUERY =
  ART_VARIANT_FILTER.map((f) => `filter=${f}`).join("&") +
  "&join=definition&sort=sortOrder,ASC&limit=200";

interface JoinedDefinition {
  id?: string;
  kind?: string;
  active?: boolean;
  createdAt?: string;
  variantAxes?: unknown;
}

type WithDefinition<T> = T & { definition?: JoinedDefinition | null };

/**
 * The variants of the one canvas definition the catalogue sells from.
 *
 * Only rows whose joined definition has kind "canvas" are kept. A row with no
 * joined definition is dropped, because its product cannot be confirmed. If
 * several canvas definitions show up (the admin "duplicate" creates an
 * inactive copy whose variants stay active), only one is used, picked the way
 * the canvas editor picks its definition: active first, then the oldest. One
 * definition matters because the page reads its option axes from the first row.
 *
 * Input order (sortOrder) is preserved.
 */
export function artPrintVariants<T extends CanvasVariant>(
  rows: ReadonlyArray<WithDefinition<T>> | null | undefined,
): WithDefinition<T>[] {
  if (!rows?.length) return [];

  const canvasRows = rows.filter(
    (row) => row?.definition?.kind === ART_DEFINITION_KIND,
  );
  if (!canvasRows.length) return [];

  // Definitions in first-seen order, so ties keep the server's ordering.
  const definitions = new Map<string, JoinedDefinition>();
  for (const row of canvasRows) {
    const def = row.definition!;
    const key = def.id ?? "";
    if (!definitions.has(key)) definitions.set(key, def);
  }
  if (definitions.size === 1) return [...canvasRows];

  const time = (d: JoinedDefinition) => {
    const t = d.createdAt ? Date.parse(d.createdAt) : NaN;
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  const [chosenId] = [...definitions.entries()]
    .map(([id, def], index) => ({ id, def, index }))
    .sort(
      (a, b) =>
        Number(b.def.active !== false) - Number(a.def.active !== false) ||
        time(a.def) - time(b.def) ||
        a.index - b.index,
    )
    .map((entry) => entry.id);

  return canvasRows.filter((row) => (row.definition!.id ?? "") === chosenId);
}
